import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { braucheSaPlatz, buildKickoffPlan, type KickoffAnderesProjekt } from './projectKickoff';
import { sendKickoffMail } from './kickoffMailer';
import type { KickoffAck, KickoffBereich, KickoffGrund, KickoffPlan } from './kickoffTypes';

/**
 * Datenzugriff und Versand-Orchestrierung für den Projekt-Kickoff
 * (Bauplan docs/projekt-kickoff-plan.md, Abschnitte 3, 4.2 und 4.2a).
 *
 * Die einzige Stelle, die mit `public.project_kickoff_acks` und den vier
 * Kickoff-RPCs (SQL Nr. 146/147) redet. Termin-Logik (`projectKickoff.ts`)
 * und Mailbau (`kickoffMailer.ts`) bleiben ohne Datenbank prüfbar; hier wird
 * nur zusammengesetzt, was ohne Supabase nicht zu haben ist.
 *
 * Hausregel wie im übrigen Repo: LESEN IST FAIL-OPEN, SCHREIBEN NICHT.
 * Fehlt die Migration, liefern alle Funktionen leere Ergebnisse — kein
 * Popup, keine Mail, aber auch kein Absturz beim Anmelden. Echte Fehler
 * (Netz, Rechte) reichen Lese- und Schreibfunktionen dagegen weiter: eine
 * leere Liste läse sich als „nichts offen", und genau dieses stille
 * Untergehen soll der Kickoff verhindern. Nur `benachrichtigeKickoff` wirft
 * nie, weil sie direkt nach der Projektanlage läuft und die Anlage nicht
 * nachträglich scheitern lassen darf.
 */

const TABELLE = 'project_kickoff_acks';

const ACK_SPALTEN =
    'id,project_id,recipient_id,bereich,grund,created_at,created_by,mail_gesendet_am,mail_fehler,' +
    'spaeter_anzahl,bestaetigt_am,bestaetigt_von,eskaliert_am,sa_platz';

/**
 * Die Spalten, die `buildKickoffPlan` vom Projekt braucht (Vertrag Paket B).
 * Das LV kommt als Join: im Blob steht `globalData.services` nach jedem
 * Speichern leer, ohne Join hätte keine Mail einen Termin.
 */
const PROJEKT_SPALTEN =
    'id,project_number,user_id,status,start_date,next_scheduled_date,data,project_services(assignment,is_optional)';

/**
 * Für die Überschneidungsprüfung genügen Turnus, Uhrzeit und Name. Bewusst
 * nur diese JSON-Pfade statt `data`: ein Koordinator kann Dutzende laufende
 * Projekte haben, und der ganze Blob je Projekt wäre für drei Felder ein
 * Vielfaches an Antwortgröße.
 */
const ANDERE_SPALTEN =
    'id,project_number,status,ic:data->metadata->intervalConfig,zeit:data->metadata->>time,name:data->metadata->>projectName';

/**
 * Höchstzahl an Ids je `.in()`-Filter. Die Liste steht in der URL; 100 uuids
 * sind rund 4 KB und bleiben sicher unter den Längengrenzen von Proxy und
 * PostgREST, auch bei einem großen CSV-Import.
 */
const IDS_JE_ABFRAGE = 100;

/**
 * Microsoft lässt dem Absenderkonto etwa 30 Mails pro Minute; 2,5 s zwischen
 * zwei Empfängern ergeben höchstens 24 und lassen Luft für parallelen Versand
 * (Berichte, Vertretungsmails) aus derselben Sitzung.
 */
const STANDARD_PAUSE_MS = 2500;

// ─── Fehlerklassen ─────────────────────────────────────────────────────────

const fehlerCode = (e: unknown): string => {
    const c = (e as { code?: unknown } | null)?.code;
    return typeof c === 'string' ? c : '';
};

/**
 * Fehlt die Migration? 42P01/PGRST205 = Tabelle unbekannt (je nach
 * Schema-Cache), PGRST202/42883 = RPC unbekannt. Alles andere ist ein echter
 * Fehler und kein Grund, still zu bleiben.
 */
export const istKickoffSchemaFehler = (e: unknown): boolean => {
    const code = fehlerCode(e);
    return code === '42P01' || code === 'PGRST205' || code === 'PGRST202' || code === '42883';
};

/**
 * EINE Warnung je Sitzung reicht: die Lesefunktionen laufen nach jeder
 * Anmeldung und bei jedem Hub-Aufruf; ohne Sperre stünde die Konsole voll
 * mit derselben Zeile.
 */
let schemaWarnungGezeigt = false;
const warneSchemaFehlt = (): void => {
    if (schemaWarnungGezeigt) return;
    schemaWarnungGezeigt = true;
    console.warn('project_kickoff_acks fehlt — SQL Nr. 146/147 noch nicht gefahren');
};

// ─── Hilfen ─────────────────────────────────────────────────────────────────

const inStuecken = <T,>(liste: T[], groesse: number): T[][] => {
    const stuecke: T[][] = [];
    for (let i = 0; i < liste.length; i += groesse) stuecke.push(liste.slice(i, i + groesse));
    return stuecke;
};

const eindeutig = (liste: string[]): string[] => Array.from(new Set(liste.filter(Boolean)));

const zahlOderNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * Zeile → `KickoffAck`. `smallint`/`integer` kommen zwar als Zahl, aber eine
 * eigene Abbildung hält die Oberfläche unabhängig davon, was PostgREST bei
 * einer späteren Typänderung liefert.
 */
const ausZeile = (row: Record<string, unknown>): KickoffAck => ({
    id: String(row.id),
    project_id: String(row.project_id ?? ''),
    recipient_id: String(row.recipient_id ?? ''),
    bereich: (row.bereich as KickoffBereich) ?? 'alle',
    grund: (row.grund as KickoffGrund) ?? 'neu',
    created_at: String(row.created_at ?? ''),
    created_by: textOderNull(row.created_by),
    mail_gesendet_am: textOderNull(row.mail_gesendet_am),
    mail_fehler: textOderNull(row.mail_fehler),
    spaeter_anzahl: zahlOderNull(row.spaeter_anzahl) ?? 0,
    bestaetigt_am: textOderNull(row.bestaetigt_am),
    bestaetigt_von: textOderNull(row.bestaetigt_von),
    eskaliert_am: textOderNull(row.eskaliert_am),
    sa_platz: zahlOderNull(row.sa_platz),
});

const warte = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Der Link in der Mail führt in dieselbe App, aus der versendet wird — auch
 * die Android-Hülle lädt die Live-Seite, ihr `origin` ist also die echte
 * Adresse. Ohne `window` (Tests, Node) bleibt der Link leer statt zu werfen.
 */
const appUrl = (): string => {
    if (typeof window === 'undefined') return '';
    try {
        return window.location?.origin ?? '';
    } catch {
        return '';
    }
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Die eigenen, noch nicht bestätigten Zeilen — die Arbeitsliste des Popups.
 * Älteste zuerst: die Sperre greift beim zuerst dreimal verschobenen Projekt,
 * und das steht so oben.
 */
export const ladeOffeneKickoffs = async (userId: string): Promise<KickoffAck[]> => {
    if (!userId) return [];
    const { data, error } = await supabase
        .from(TABELLE)
        .select(ACK_SPALTEN)
        .eq('recipient_id', userId)
        .is('bestaetigt_am', null)
        .order('created_at', { ascending: true });
    if (error) {
        if (istKickoffSchemaFehler(error)) {
            warneSchemaFehlt();
            return [];
        }
        throw error;
    }
    return ((data as Record<string, unknown>[] | null) ?? []).map(ausZeile);
};

/**
 * Alle Zeilen dieser Projekte (bestätigte und offene), für Hub und
 * Admin-Liste. Welche davon der Nutzer sieht, entscheidet die RLS — ein
 * nachgebauter Filter im Client wäre eine zweite Wahrheit.
 */
export const ladeKickoffsFuerProjekte = async (projectIds: string[]): Promise<KickoffAck[]> => {
    const ids = eindeutig(projectIds);
    if (ids.length === 0) return [];
    const alle: KickoffAck[] = [];
    for (const stueck of inStuecken(ids, IDS_JE_ABFRAGE)) {
        const { data, error } = await supabase
            .from(TABELLE)
            .select(ACK_SPALTEN)
            .in('project_id', stueck)
            .order('created_at', { ascending: true });
        if (error) {
            if (istKickoffSchemaFehler(error)) {
                warneSchemaFehlt();
                return [];
            }
            throw error;
        }
        alle.push(...((data as Record<string, unknown>[] | null) ?? []).map(ausZeile));
    }
    return alle;
};

// ─── Schreiben (RPCs) ───────────────────────────────────────────────────────

/**
 * „Später" für alle gerade gezeigten Projekte. Der Server zählt atomar und
 * lehnt ab Stand 3 ab; zurück kommt der neue Stand je Zeile, damit das Popup
 * nicht selbst rechnen muss (zweites Gerät, Neuladen).
 *
 * `erhoeht` sagt, ob DIESER Aufruf die Zeile tatsächlich hochgezählt hat.
 * Eine Zeile, die schon bei 3 stand, kommt mit `false` zurück und darf im
 * Dialog nicht verschwinden — sonst wäre die Sperre per Klick umgangen.
 * Fehlt das Feld (Antwort einer älteren RPC-Fassung), gilt `false`: lieber
 * bleibt eine Zeile einmal zu oft stehen, als dass sie ungezählt
 * ausgeblendet wird.
 */
export const kickoffSpaeter = async (
    ids: string[],
): Promise<Array<{ id: string; spaeter_anzahl: number; erhoeht: boolean }>> => {
    const p_ids = eindeutig(ids);
    if (p_ids.length === 0) return [];
    const { data, error } = await supabase.rpc('kickoff_spaeter', { p_ids });
    if (error) {
        if (istKickoffSchemaFehler(error)) {
            warneSchemaFehlt();
            return [];
        }
        throw error;
    }
    if (!Array.isArray(data)) return [];
    return (data as Array<Record<string, unknown>>).map((z) => ({
        id: String(z?.id ?? ''),
        spaeter_anzahl: zahlOderNull(z?.spaeter_anzahl) ?? 0,
        erhoeht: z?.erhoeht === true,
    }));
};

export const kickoffBestaetigen = async (id: string): Promise<void> => {
    const { error } = await supabase.rpc('kickoff_bestaetigen', { p_id: id });
    if (error) {
        if (istKickoffSchemaFehler(error)) {
            warneSchemaFehlt();
            return;
        }
        throw error;
    }
};

/** `fehler = null` heißt „zugestellt"; der Server setzt dann `mail_gesendet_am`. */
export const kickoffMailVermerken = async (id: string, fehler: string | null): Promise<void> => {
    const { error } = await supabase.rpc('kickoff_mail_vermerken', { p_id: id, p_fehler: fehler });
    if (error) {
        if (istKickoffSchemaFehler(error)) {
            warneSchemaFehlt();
            return;
        }
        throw error;
    }
};

/**
 * Nächster freier Halbstunden-Platz der Samstagsserie für diese Zeile. Der
 * Server vergibt ihn (und liefert einen schon vergebenen unverändert), damit
 * zwei Geräte oder Popup und Mail nie verschiedene Uhrzeiten nennen.
 *
 * null = kein Platz: Migration fehlt oder alle 32 Plätze des Samstags sind
 * belegt. Die Termin-Logik lässt die SiGe-Plan-Serie dann weg und meldet das
 * als Hinweis.
 */
export const kickoffSaPlatzVergeben = async (id: string): Promise<number | null> => {
    const { data, error } = await supabase.rpc('kickoff_sa_platz_vergeben', { p_id: id });
    if (error) {
        if (istKickoffSchemaFehler(error)) {
            warneSchemaFehlt();
            return null;
        }
        throw error;
    }
    return zahlOderNull(data);
};

// ─── Pläne ──────────────────────────────────────────────────────────────────

export interface KickoffEintrag {
    ack: KickoffAck;
    /** null = Projekt nicht ladbar (gelöscht, keine Rechte) oder Plan nicht baubar — dann steht `fehler`. */
    plan: KickoffPlan | null;
    /**
     * Warum `plan` fehlt: Projektabfrage gescheitert, Projekt nicht in der
     * Antwort oder Termin-Logik gescheitert. Ohne Fehler fehlt das Feld.
     *
     * Der Dialog braucht die Unterscheidung: ein gesperrter Dialog darf den
     * Nutzer nicht zwingen, ein Projekt zu „bestätigen", dessen Termine er
     * nie gesehen hat. Eine gescheiterte Samstagsplatz-Vergabe ist KEIN
     * Fehler — die meldet die Termin-Logik als Hinweis `sigeplan_ohne_platz`.
     */
    fehler?: string;
}

const PROJEKT_NICHT_LADBAR = 'Projekt nicht ladbar';

interface GeladeneProjekte {
    projekte: Map<string, Record<string, unknown>>;
    /** Ids aus einem Abfrage-Stück, das technisch scheiterte, mit dem Fehlertext. */
    fehler: Map<string, string>;
}

/**
 * Projekte nach Id. Ein Fehler hier ist kein Grund, das Popup scheitern zu
 * lassen: die Quittung hängt an der Zeile, nicht am Plan. Die betroffenen
 * Einträge kommen mit `plan: null` und dem Fehlertext, damit der Dialog
 * „nicht ladbar" von „geladen" unterscheiden kann.
 */
const ladeProjekte = async (projectIds: string[]): Promise<GeladeneProjekte> => {
    const projekte = new Map<string, Record<string, unknown>>();
    const fehler = new Map<string, string>();
    const ids = eindeutig(projectIds);
    for (const stueck of inStuecken(ids, IDS_JE_ABFRAGE)) {
        try {
            const { data, error } = await supabase.from('projects').select(PROJEKT_SPALTEN).in('id', stueck);
            if (error) throw error;
            for (const row of (data as Record<string, unknown>[] | null) ?? []) {
                projekte.set(String(row.id), row);
            }
        } catch (e) {
            const text = `${PROJEKT_NICHT_LADBAR} — ${describeError(e)}`;
            console.warn('Kickoff:', text);
            for (const id of stueck) fehler.set(id, text);
        }
    }
    return { projekte, fehler };
};

/**
 * Plan für eine Zeile. Braucht er einen Samstagsplatz und hat noch keinen,
 * wird der VOR dem Planbau vergeben: sonst zeigte das Popup 08:00 und die
 * Mail (die ihn später vergibt) 08:30 — oder umgekehrt.
 *
 * Scheitert die Vergabe oder ist kein Platz frei, geht `saPlatz: null` an
 * die Termin-Logik. Die lässt die SiGe-Plan-Serie weg und setzt den Hinweis
 * `sigeplan_ohne_platz` — eine erfundene Uhrzeit (alles auf 08:00) wäre
 * genau das Übereinanderliegen, das die Staffelung verhindern soll. Die
 * übrigen Termine bleiben; beim nächsten Laden wird die Vergabe erneut
 * versucht.
 */
const baueEintrag = async (
    ack: KickoffAck,
    geladen: GeladeneProjekte,
    andereProjekte?: KickoffAnderesProjekt[],
): Promise<KickoffEintrag> => {
    const projekt = geladen.projekte.get(ack.project_id);
    if (!projekt) return { ack, plan: null, fehler: geladen.fehler.get(ack.project_id) ?? PROJEKT_NICHT_LADBAR };
    let aktuell = ack;
    let saPlatz = ack.sa_platz;
    try {
        if (saPlatz === null && braucheSaPlatz(projekt, ack.bereich)) {
            const platz = await kickoffSaPlatzVergeben(ack.id);
            if (platz !== null) {
                saPlatz = platz;
                aktuell = { ...ack, sa_platz: platz };
            }
        }
    } catch (e) {
        console.warn('Kickoff: Samstagsplatz nicht vergeben —', describeError(e));
    }
    try {
        const plan = buildKickoffPlan(projekt, {
            bereich: ack.bereich,
            saPlatz,
            ...(andereProjekte ? { andereProjekte } : {}),
        });
        return { ack: aktuell, plan };
    } catch (e) {
        const fehler = `Termine nicht berechenbar — ${describeError(e)}`;
        console.warn('Kickoff:', fehler);
        return { ack: aktuell, plan: null, fehler };
    }
};

/**
 * Pläne zu den Zeilen, in derselben Reihenfolge. Die zurückgegebene `ack`
 * trägt einen frisch vergebenen `sa_platz` schon mit.
 *
 * Nacheinander, nicht parallel: zwei gleichzeitige Platzvergaben desselben
 * Koordinators sollen der Reihe nach 08:00 und 08:30 bekommen, nicht vom
 * Server gegeneinander ausgespielt werden.
 */
export const ladeKickoffPlaene = async (acks: KickoffAck[]): Promise<KickoffEintrag[]> => {
    if (acks.length === 0) return [];
    const geladen = await ladeProjekte(acks.map((a) => a.project_id));
    const eintraege: KickoffEintrag[] = [];
    for (const ack of acks) {
        eintraege.push(await baueEintrag(ack, geladen));
    }
    return eintraege;
};

// ─── Versand ────────────────────────────────────────────────────────────────

export interface KickoffVersandErgebnis {
    recipientId: string;
    email: string;
    projektIds: string[];
    ok: boolean;
    error?: string;
}

export interface KickoffVersandOptionen {
    /** Steht in der Mail als „Angelegt von …". */
    erstellerName?: string;
    /** Auch Zeilen, deren Mail schon raus ist („Mail erneut senden"). */
    erneut?: boolean;
    /** Nur an diesen Empfänger (Popup: „Mail erneut senden" für mich). */
    nurEmpfaenger?: string;
    /** Wartezeit zwischen zwei Mails in ms, Standard 2500. Tests: 0. */
    pauseMs?: number;
}

interface Profil {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
}

const ladeVersandZeilen = async (projectIds: string[], opt: KickoffVersandOptionen): Promise<KickoffAck[]> => {
    const alle: KickoffAck[] = [];
    for (const stueck of inStuecken(eindeutig(projectIds), IDS_JE_ABFRAGE)) {
        let query = supabase.from(TABELLE).select(ACK_SPALTEN).in('project_id', stueck).is('bestaetigt_am', null);
        if (!opt.erneut) query = query.is('mail_gesendet_am', null);
        if (opt.nurEmpfaenger) query = query.eq('recipient_id', opt.nurEmpfaenger);
        const { data, error } = await query.order('created_at', { ascending: true });
        if (error) throw error;
        alle.push(...((data as Record<string, unknown>[] | null) ?? []).map(ausZeile));
    }
    return alle;
};

/**
 * Profile der Empfänger. Scheitert das, fehlt eben die Adresse — die
 * Empfänger landen mit „keine E-Mail-Adresse" im Ergebnis und in
 * `mail_fehler`, statt den ganzen Versand abzubrechen.
 */
const ladeProfile = async (ids: string[]): Promise<Map<string, Profil>> => {
    const profile = new Map<string, Profil>();
    if (ids.length === 0) return profile;
    try {
        const { data, error } = await supabase.from('profiles').select('id,first_name,last_name,email').in('id', ids);
        if (error) throw error;
        for (const p of (data as Profil[] | null) ?? []) profile.set(String(p.id), p);
    } catch (e) {
        console.warn('Kickoff: Empfängerprofile nicht ladbar —', describeError(e));
    }
    return profile;
};

const zuAnderemProjekt = (row: Record<string, unknown>): KickoffAnderesProjekt => {
    const ic = row.ic && typeof row.ic === 'object' ? (row.ic as Record<string, unknown>) : null;
    const anderes: KickoffAnderesProjekt = {
        id: String(row.id),
        projectNumber: String(row.project_number ?? ''),
        projectName: typeof row.name === 'string' ? row.name : '',
    };
    if (typeof ic?.type === 'string') anderes.intervalType = ic.type;
    if (Array.isArray(ic?.preferredWeekdays)) {
        anderes.weekdays = (ic.preferredWeekdays as unknown[]).map(Number).filter((n) => Number.isInteger(n));
    }
    // Ohne feste Wochentage bekommt ein Projekt keinen Ersatz-Wochentag: seine
    // Serie ist ein ganztägiger, freier Montags-Merker (`wochenMerker`) und
    // überschneidet sich mit nichts.
    if (typeof row.zeit === 'string' && row.zeit !== '') anderes.uhrzeit = row.zeit;
    return anderes;
};

/**
 * Laufende Projekte des Empfängers für die Überschneidungsprüfung. Nur
 * `user_id`: Besitzer ist der Koordinator Ausführung, und nur dessen
 * Begehungsserien können sich überschneiden.
 *
 * null = nicht ladbar (z. B. RLS: ein Nicht-Admin legt für einen Kollegen an
 * und sieht dessen übrige Projekte nicht). Dann geht die Mail ohne
 * Überschneidungshinweis raus — der Hinweis ist eine Hilfe, kein Riegel.
 */
const ladeAndereProjekte = async (recipientId: string): Promise<KickoffAnderesProjekt[] | null> => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select(ANDERE_SPALTEN)
            .eq('user_id', recipientId)
            .in('status', ['planning', 'active'])
            // Übungsprojekte aus dem Lernzentrum (Nr. 100) sind keine echten
            // Begehungen und dürfen keinen Überschneidungshinweis auslösen.
            .not('ist_uebung', 'is', true);
        if (error) throw error;
        return ((data as Record<string, unknown>[] | null) ?? []).map(zuAnderemProjekt);
    } catch (e) {
        console.warn('Kickoff: Überschneidungsprüfung entfällt —', describeError(e));
        return null;
    }
};

const empfaengerName = (p: Profil | undefined, email: string): string => {
    const name = `${p?.first_name ?? ''} ${p?.last_name ?? ''}`.trim();
    return name || email;
};

const vermerke = async (ids: string[], fehler: string | null): Promise<void> => {
    for (const id of ids) {
        try {
            await kickoffMailVermerken(id, fehler);
        } catch (e) {
            // Die Mail ist raus (oder eben nicht) — ein gescheiterter Vermerk
            // ändert daran nichts. Der Nachtlauf sieht dann nur kein Datum.
            console.warn('Kickoff: Mailstatus nicht vermerkt —', describeError(e));
        }
    }
};

/**
 * Kickoff-Mails zu diesen Projekten: EINE Mail je Empfänger mit allen seinen
 * Projekten und einer gemeinsamen Kalenderdatei (Bauplan 4.2a).
 *
 * Wirft nie. Sie läuft direkt nach der Projektanlage; ein Fehler hier darf
 * die Anlage nicht nachträglich scheitern lassen. Was schiefgeht, steht im
 * Ergebnis und über `kickoff_mail_vermerken` in der Zeile — dort liest es der
 * Hub („Mail nicht zugestellt") und die Eskalation im Nachtlauf.
 *
 * Fehlt die Migration, kommt `[]`. Ein ECHTER Fehler beim Laden der Zeilen
 * kommt als ein Eintrag ohne Empfänger (`recipientId: ''`) zurück: eine leere
 * Liste läse sich im Hub als „niemand offen", und der Knopf „erneut senden"
 * meldete Erfolg, wo nichts versucht wurde.
 */
export const benachrichtigeKickoff = async (
    projectIds: string[],
    opt: KickoffVersandOptionen = {},
): Promise<KickoffVersandErgebnis[]> => {
    const ids = eindeutig(projectIds ?? []);
    if (ids.length === 0) return [];
    const pauseMs = opt.pauseMs ?? STANDARD_PAUSE_MS;

    let zeilen: KickoffAck[];
    try {
        zeilen = await ladeVersandZeilen(ids, opt);
    } catch (e) {
        if (istKickoffSchemaFehler(e)) {
            warneSchemaFehlt();
            return [];
        }
        console.warn('Kickoff: Versandzeilen nicht ladbar —', describeError(e));
        return [{ recipientId: '', email: '', projektIds: ids, ok: false, error: describeError(e) }];
    }
    if (zeilen.length === 0) return [];

    // Je Empfänger bündeln; die Reihenfolge folgt der ältesten Zeile.
    const jeEmpfaenger = new Map<string, KickoffAck[]>();
    for (const z of zeilen) {
        const liste = jeEmpfaenger.get(z.recipient_id) ?? [];
        liste.push(z);
        jeEmpfaenger.set(z.recipient_id, liste);
    }

    let geladen: GeladeneProjekte = { projekte: new Map(), fehler: new Map() };
    let profile = new Map<string, Profil>();
    try {
        geladen = await ladeProjekte(zeilen.map((z) => z.project_id));
        profile = await ladeProfile(Array.from(jeEmpfaenger.keys()));
    } catch (e) {
        // Beide Lader fangen selbst; das hier ist nur der Gürtel zu den Hosenträgern.
        console.warn('Kickoff: Vorbereitung unvollständig —', describeError(e));
    }

    const ergebnisse: KickoffVersandErgebnis[] = [];
    let schonGesendet = false;

    for (const [recipientId, acks] of jeEmpfaenger) {
        const projektIds = eindeutig(acks.map((a) => a.project_id));
        const profil = profile.get(recipientId);
        const email = (profil?.email ?? '').trim();
        try {
            if (!email) {
                const error = 'Keine E-Mail-Adresse beim Empfänger hinterlegt';
                await vermerke(acks.map((a) => a.id), error);
                ergebnisse.push({ recipientId, email: '', projektIds, ok: false, error });
                continue;
            }

            // Nur wer Begehungen bekommt, kann Überschneidungen haben — reine
            // Planungszeilen sparen sich die Abfrage.
            const andere = acks.some((a) => a.bereich !== 'planung') ? await ladeAndereProjekte(recipientId) : null;
            const mitPlan: Array<{ ack: KickoffAck; plan: KickoffPlan }> = [];
            const ohnePlan: Array<{ ack: KickoffAck; fehler: string }> = [];
            for (const ack of acks) {
                // Das Projekt selbst fliegt aus der Vergleichsliste, sonst
                // überschnitte es sich mit sich selbst. Die übrigen Projekte
                // DIESES Vorgangs bleiben drin: drei per CSV angelegte
                // Baustellen mit Dienstag 08:00 sind genau der Fall, den der
                // Hinweis melden soll.
                const vergleich = andere?.filter((p) => p.id !== ack.project_id);
                const eintrag = await baueEintrag(ack, geladen, vergleich);
                if (eintrag.plan) mitPlan.push({ ack: eintrag.ack, plan: eintrag.plan });
                else ohnePlan.push({ ack: eintrag.ack, fehler: eintrag.fehler ?? PROJEKT_NICHT_LADBAR });
            }

            if (mitPlan.length === 0) {
                const error = 'Projekt nicht ladbar — keine Termine für die Mail';
                await vermerke(ohnePlan.map((o) => o.ack.id), error);
                ergebnisse.push({ recipientId, email, projektIds, ok: false, error });
                continue;
            }

            if (schonGesendet && pauseMs > 0) await warte(pauseMs);
            schonGesendet = true;

            let antwort: { ok: boolean; error?: string };
            try {
                antwort = await sendKickoffMail({
                    empfaengerEmail: email,
                    empfaengerName: empfaengerName(profil, email),
                    plaene: mitPlan.map((m) => m.plan),
                    erstellerName: opt.erstellerName,
                    appUrl: appUrl(),
                });
            } catch (e) {
                antwort = { ok: false, error: describeError(e) };
            }
            const fehler = antwort.ok ? null : antwort.error || 'Versand fehlgeschlagen';

            await vermerke(mitPlan.map((m) => m.ack.id), fehler);
            // Zeilen ohne Plan standen nicht in der Mail — das gehört in die
            // Zeile, sonst hielte der Hub sie für zugestellt. Der Grund kommt
            // mit, damit „nicht ladbar" und „Termine nicht berechenbar"
            // auseinanderzuhalten sind.
            for (const o of ohnePlan) await vermerke([o.ack.id], `${o.fehler} — nicht in der Mail`);

            ergebnisse.push(
                fehler === null
                    ? { recipientId, email, projektIds, ok: true }
                    : { recipientId, email, projektIds, ok: false, error: fehler },
            );
        } catch (e) {
            ergebnisse.push({ recipientId, email, projektIds, ok: false, error: describeError(e) });
        }
    }

    return ergebnisse;
};
